import { ProductionCardStatus } from '@app/models/production/production-card';
import { ProductionCardRoles } from '@app/models/roles/roles-group';

/**
 * Statuses each role may assign to a Production Card. Must stay in sync with
 * ProductionCardStatusRoleService in Production.API, which enforces the same map.
 * Dyeing, ReadyForFinishing, FinishedGood and Shipped are set by the system, so no role assigns them by hand.
 */
export const PRODUCTION_CARD_STATUS_ROLES: Readonly<Partial<Record<ProductionCardRoles, readonly ProductionCardStatus[]>>> = {
  [ProductionCardRoles.ProductionCardPlanningStatusEditor]: [
    ProductionCardStatus.Draft,
    ProductionCardStatus.Submitted,
    ProductionCardStatus.Approved,
    ProductionCardStatus.Deleted,
    ProductionCardStatus.Rejected,
  ],
  [ProductionCardRoles.ProductionCardGreigeStatusEditor]: [
    ProductionCardStatus.PlanningForBatching,
    ProductionCardStatus.InRouteToBatching,
  ],
  [ProductionCardRoles.ProductionCardBatchingStatusEditor]: [ProductionCardStatus.Batching, ProductionCardStatus.InRouteToBrd],
  [ProductionCardRoles.ProductionCardDyeingStatusEditor]: [ProductionCardStatus.Brd],
  [ProductionCardRoles.ProductionCardFinishingStatusEditor]: [ProductionCardStatus.Finishing],
  [ProductionCardRoles.ProductionCardsCloser]: [ProductionCardStatus.Closed, ProductionCardStatus.Reassignment],
};

/** Union of the statuses the given roles may assign. */
export function getAssignableStatuses(userRoles: readonly string[]): ProductionCardStatus[] {
  const assignable = new Set<ProductionCardStatus>();
  for (const [role, statuses] of Object.entries(PRODUCTION_CARD_STATUS_ROLES)) {
    if (userRoles.includes(role)) {
      statuses.forEach((status) => assignable.add(status));
    }
  }
  return [...assignable];
}

/** Keeps the candidates the given roles may assign, in the candidates' order. */
export function filterAssignableStatuses(
  candidates: readonly ProductionCardStatus[],
  userRoles: readonly string[],
): ProductionCardStatus[] {
  const assignable = getAssignableStatuses(userRoles);
  return candidates.filter((status) => assignable.includes(status));
}
